import React, { useState, useEffect } from 'react';
import { X, Upload, Fuel, CreditCard, ParkingCircle, Utensils, Package, Wrench, DollarSign, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const RouteExpensesModal = ({ isOpen, onClose, routeId }) => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({
        expense_type: 'fuel',
        amount: '',
        description: '',
        photo: null
    });
    const [photoPreview, setPhotoPreview] = useState(null);

    const expenseTypes = [
        { value: 'fuel', label: 'Combustible', icon: Fuel },
        { value: 'toll', label: 'Peaje', icon: CreditCard },
        { value: 'parking', label: 'Parqueadero', icon: ParkingCircle },
        { value: 'food', label: 'Alimentación', icon: Utensils },
        { value: 'errands', label: 'Diligencias', icon: Package },
        { value: 'maintenance', label: 'Mantenimiento', icon: Wrench },
        { value: 'other', label: 'Otro', icon: DollarSign }
    ];

    useEffect(() => {
        if (isOpen && routeId) {
            loadExpenses();
        }
    }, [isOpen, routeId]);

    const loadExpenses = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/routes/${routeId}/expenses`);
            setExpenses(response.data.expenses || []);
        } catch (error) {
            console.error('Error loading expenses:', error);
            toast.error('Error al cargar gastos');
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, photo: file });
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            toast.error('Ingresa un monto válido');
            return;
        }

        try {
            setLoading(true);
            const submitData = new FormData();
            submitData.append('expense_type', formData.expense_type);
            submitData.append('amount', formData.amount);
            if (formData.description) {
                submitData.append('description', formData.description);
            }
            if (formData.photo) {
                submitData.append('photo', formData.photo);
            }

            await api.post(`/routes/${routeId}/expenses`, submitData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success('✅ Gasto registrado');
            setFormData({ expense_type: 'fuel', amount: '', description: '', photo: null });
            setPhotoPreview(null);
            setShowAddForm(false);
            loadExpenses();
        } catch (error) {
            console.error('Error creating expense:', error);
            toast.error(error.response?.data?.message || 'Error al registrar gasto');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (expenseId) => {
        if (!window.confirm('¿Eliminar este gasto?')) return;

        try {
            await api.delete(`/routes/${routeId}/expenses/${expenseId}`);
            toast.success('Gasto eliminado');
            loadExpenses();
        } catch (error) {
            console.error('Error deleting expense:', error);
            toast.error('Error al eliminar gasto');
        }
    };

    const getTotalExpenses = () => {
        return expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    };

    const getExpenseIcon = (type) => {
        const expenseType = expenseTypes.find(t => t.value === type);
        return expenseType ? expenseType.icon : DollarSign;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-blue-50">
                    <h2 className="text-xl font-bold text-gray-900">💰 Gastos de Ruta</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">

                    {/* Total */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg">
                        <div className="text-sm opacity-90">Total Gastos</div>
                        <div className="text-3xl font-bold">
                            ${getTotalExpenses().toLocaleString('es-CO')}
                        </div>
                        <div className="text-sm opacity-75">{expenses.length} gasto{expenses.length !== 1 ? 's' : ''} registrado{expenses.length !== 1 ? 's' : ''}</div>
                    </div>

                    {/* Add Button */}
                    {!showAddForm && (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="w-full mb-4 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors"
                        >
                            + Agregar Gasto
                        </button>
                    )}

                    {/* Add Form */}
                    {showAddForm && (
                        <form onSubmit={handleSubmit} className="mb-6 p-4 border-2 border-green-200 rounded-lg bg-green-50">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-gray-900">Nuevo Gasto</h3>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setFormData({ expense_type: 'fuel', amount: '', description: '', photo: null });
                                        setPhotoPreview(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Expense Type */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Gasto</label>
                                <select
                                    value={formData.expense_type}
                                    onChange={(e) => setFormData({ ...formData, expense_type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                >
                                    {expenseTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Amount */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Monto *</label>
                                <input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    placeholder="0"
                                    step="100"
                                    min="0"
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                            </div>

                            {/* Description */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción (opcional)</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Ej: Peaje autopista Norte"
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                            </div>

                            {/* Photo Upload */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Foto de Evidencia (opcional)</label>
                                <div className="flex items-center gap-3">
                                    <label className="flex-1 cursor-pointer">
                                        <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-center hover:bg-gray-200 transition-colors">
                                            <Upload className="w-5 h-5 mx-auto mb-1" />
                                            <span className="text-sm text-gray-700">
                                                {formData.photo ? formData.photo.name : 'Subir Foto'}
                                            </span>
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                                {photoPreview && (
                                    <img src={photoPreview} alt="Preview" className="mt-2 w-full h-32 object-cover rounded-lg" />
                                )}
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                            >
                                {loading ? 'Guardando...' : 'Guardar Gasto'}
                            </button>
                        </form>
                    )}

                    {/* Expenses List */}
                    {loading && expenses.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">Cargando...</div>
                    ) : expenses.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>No hay gastos registrados</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {expenses.map((expense) => {
                                const Icon = getExpenseIcon(expense.expense_type);
                                const expenseType = expenseTypes.find(t => t.value === expense.expense_type);

                                return (
                                    <div key={expense.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3 flex-1">
                                                <div className="p-2 bg-blue-100 rounded-lg">
                                                    <Icon className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-semibold text-gray-900">{expenseType?.label || expense.expense_type}</div>
                                                    {expense.description && (
                                                        <div className="text-sm text-gray-600 mt-1">{expense.description}</div>
                                                    )}
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        {new Date(expense.created_at).toLocaleString('es-CO')}
                                                        {expense.created_by_name && ` • ${expense.created_by_name}`}
                                                    </div>
                                                    {expense.receipt_photo && (
                                                        <button
                                                            onClick={() => window.open(`/uploads/evidence/expenses/${expense.receipt_photo}`, '_blank')}
                                                            className="text-xs text-blue-600 hover:underline mt-1"
                                                        >
                                                            📷 Ver evidencia
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right ml-4">
                                                <div className="text-xl font-bold text-gray-900">
                                                    ${parseFloat(expense.amount).toLocaleString('es-CO')}
                                                </div>
                                                <button
                                                    onClick={() => handleDelete(expense.id)}
                                                    className="text-red-500 hover:text-red-700 text-xs mt-1"
                                                >
                                                    <Trash2 className="w-4 h-4 inline" /> Eliminar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
                    >
                        Cerrar
                    </button>
                </div>

            </div>
        </div>
    );
};

export default RouteExpensesModal;
